import { HTMLAttributes, ReactNode, forwardRef, useState } from 'react';

import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core';

import classNames from 'classnames';

import { ActivityFragment } from '@spotify/confidence-graphql';

import { ActivityFeedItemContext, ReactionSummary } from './context';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      borderRadius: theme.shape.borderRadius * 2,
      padding: theme.spacing(3),
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      '&:not($disableBorder)': {
        boxShadow: `0 0 0 1px ${theme.palette.divider}`,
      },
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    disableBorder: {
      boxShadow: 'none',
    },
  });

export interface RootProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'classes'>,
    WithStyles<typeof styles> {
  children: ReactNode;
  disableBorder?: boolean;
  activity: Pick<ActivityFragment, 'name' | 'activityTime' | 'actor'>;
  reactions?: ReactionSummary;
}

const Root = forwardRef<HTMLDivElement, RootProps>((props, ref) => {
  const {
    className,
    classes,
    children,
    disableBorder,
    activity,
    reactions = { commentCount: 0 },
    ...rest
  } = props;
  const [showComments, setShowComments] = useState(false);

  return (
    <ActivityFeedItemContext.Provider
      value={{ showComments, setShowComments, activity, reactions }}
    >
      <div
        ref={ref}
        className={classNames(classes.root, className, {
          [classes.disableBorder]: disableBorder,
        })}
        {...rest}
      >
        {children}
      </div>
    </ActivityFeedItemContext.Provider>
  );
});

Root.displayName = 'ActivityFeedItem.Root';

export default withStyles(styles)(Root);
